import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import { CreateButton } from "../../components/Core/Button/Button";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Pagination from "../../components/Shared/Pagination/Pagination";
import { CorporateGroupBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import useDebounce from "../../hooks/useDebounce";
import useQueryState from "../../hooks/useQueryState";
import { useCorporateGroups } from "../../networking/corporateGroup.hooks";
import CorporateGroupFormModal from "./CorporateGroupFormModal";

/**
 * Corporate groups (koncerner) admin page — reached from the customers list's
 * "..." overflow menu ("Hantera koncernföretag"). Lists all groups with member
 * counts; rows open the edit modal, which is where the shared invoice details
 * are updated "in one place".
 */
const CorporateGroupsPage: React.FC = () => {
	const { t } = useTranslation();

	const [searchTerm, setSearchTerm] = useQueryState<string>("search", { initialValue: "" });
	const [page, setPage] = useState(1);
	const [editGroupId, setEditGroupId] = useState<string>();
	const [showCreateModal, setShowCreateModal] = useState(false);

	const keyword = useDebounce(searchTerm, 250);

	const { corporateGroups, isLoading } = useCorporateGroups({
		page,
		pageSize: PAGE_SIZE,
		keyword,
		sort: "name",
	});

	const groups = corporateGroups?.corporateGroups || [];
	const totalCount = corporateGroups?.totalCount || 0;

	return (
		<>
			<Breadcrumbs items={CorporateGroupBreadcrumbs} />

			<div className="px-4 pt-4">
				<div className="flex items-center gap-5 mb-3">
					<div className="w-[240px] shrink-0">
						<AppInputNew
							containerClassName="[&>p:last-child]:hidden"
							placeholder={t("SEARCH")}
							icon="search"
							value={searchTerm || ""}
							onChange={(e: any) => {
								setSearchTerm(e.target.value);
								setPage(1);
							}}
						/>
					</div>

					<div className="ml-auto flex items-center gap-2">
						<CreateButton height="33px" onClick={() => setShowCreateModal(true)}>
							{t("corporateGroup.create")}
						</CreateButton>
					</div>
				</div>
			</div>

			<div className="px-4">
				{isLoading ? (
					<LoadingIndicator className="mt-4" />
				) : groups.length > 0 ? (
					<TableLayout>
						<table>
							<thead>
								<tr>
									<th style={{ width: 300 }}>{t("corporateGroup.name")}</th>
									<th style={{ width: 200 }}>{t("INVOICE_ADDRESS")}</th>
									<th style={{ width: 150 }}>{t("customer.gln")}</th>
									<th style={{ width: 120 }}>{t("corporateGroup.memberCount")}</th>
								</tr>
							</thead>
							<tbody>
								{groups.map((group) => (
									<tr key={group.id} onClick={() => setEditGroupId(group.id)}>
										<td>{group.name}</td>
										<td>
											{[group.invoiceDetails?.address1, group.invoiceDetails?.city]
												.filter(Boolean)
												.join(", ")}
										</td>
										<td>{group.invoiceDetails?.GLNCode}</td>
										<td>{group.memberCount}</td>
									</tr>
								))}
							</tbody>
						</table>
					</TableLayout>
				) : (
					<EmptyState margin="2rem 0" label="corporateGroup.empty" />
				)}

				{totalCount > PAGE_SIZE && (
					<Pagination currentPage={page} totalCount={totalCount} onClickItem={(p) => setPage(p)} />
				)}
			</div>

			{showCreateModal && <CorporateGroupFormModal onClose={() => setShowCreateModal(false)} />}

			{editGroupId && (
				<CorporateGroupFormModal
					corporateGroupId={editGroupId}
					onClose={() => setEditGroupId(undefined)}
				/>
			)}
		</>
	);
};

export default CorporateGroupsPage;
